import { Box } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useQueryClient } from "@tanstack/react-query";
import { memo, useState } from "react";
import { useParams } from "react-router-dom";
import CustomDataGridV2 from "@/components/CustomDataGridV2";
import Flex from "@/components/Flex";
import PdfViewDrawer from "@/components/PdfViewDrawer";
import RenderWithTooltip from "@/components/RenderWithTooltip";
import StatusChip from "@/components/StatusChip";
import ActionMenu from "@/components/app/ActionMenu";
import CustomTabs from "@/components/app/CustomTabs";
import DeleteModal from "@/components/app/DeleteModal";
import { DSCheckbox } from "@/components/ds/DSCheckbox";
import { useReduceDisclosure } from "@/hooks/use-reduce-disclosure";
import { useArchiveState } from "@/hooks/useArchiveState";
import { useAuthorizationDownload } from "@/queries/patient-chart/demographics/authorization/use-athorization-download";
import { useAuthorizationArchive } from "@/queries/patient-chart/demographics/authorization/use-authorization-archive";
import { useAuthorizationList } from "@/queries/patient-chart/demographics/authorization/use-get-all-authorization";
import { NonNullableDate } from "@/utils/date-time-utility";
import {
  DEFAULT_VALUE_LABEL_LIST,
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
  getAllValuesFromObj,
  getIdFromObj,
  getValueFromObj,
  getXtenantId,
  replaceUnderScoreAddSpace,
  transformMultiSelectFrontEndVal,
} from "@/utils/utils";
import UpsertAuthorizationModal from "./UpsertAuthorizationModal";
import {
  AUTHORIZATION_ACTION,
  AUTHORIZATION_LIST_QUERY_KEY,
  AUTHORIZATION_STATUS,
  SHOW_ARCHIVED_LABEL,
  SHOW_INACTIVE_LABEL,
  STATUS_COLOR_MAP,
  AuthorizationStatus,
} from "./authorization-constant";
import { getAuthorizationActionOptions } from "./authorization-utils";

const Authorization = () => {
  const [activeTab, setActiveTab] = useState<string>("active");
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const isAllTab = activeTab === "active";
  // All tab: the checkbox swaps Active for Inactive. Expired tab: it shows the archived ones.
  const authorizationStatus =
    isAllTab && !isChecked
      ? AUTHORIZATION_STATUS.ACTIVE
      : AUTHORIZATION_STATUS.INACTIVE;
  const isArchived = !isAllTab && isChecked;

  return (
    <Flex flexDirection={"column"} gap={2} height={"100%"}>
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        paddingX={"10px"}
        height={"40px"}
      >
        <CustomTabs
          tabsData={AUTHORIZATION_TABS}
          value={activeTab}
          onChange={(v) => {
            setActiveTab(v);
            setIsChecked(false);
          }}
        />

        <DSCheckbox
          label={isAllTab ? SHOW_INACTIVE_LABEL : SHOW_ARCHIVED_LABEL}
          checked={isChecked}
          onChange={(e) => setIsChecked(e.target.checked)}
        />
      </Box>
      <ActiveAuthorization
        authorizationStatus={authorizationStatus}
        isArchived={isArchived}
      />
    </Flex>
  );
};

export default Authorization;

const AUTHORIZATION_TABS = [
  { value: "active", label: "All Prior Authorizations" },
  { value: "expired", label: "Expired Prior Authorizations" },
];

const MINIMIZED_AUTHORIZATION_FIELDS = [
  "authorizationNumber",
  "insurancePayer",
  "authorizationStatus",
  "actions",
];

interface ActiveAuthorizationProps {
  authorizationStatus: AuthorizationStatus;
  isArchived: boolean;
}

const ActiveAuthorization = memo(
  ({ authorizationStatus, isArchived }: ActiveAuthorizationProps) => {
    const isVisitNotePage = window.location.pathname.includes("/visit-note");
    const [authorizationDisclosure, deleteDisc, previewResultDisc] =
      useReduceDisclosure(3);
    const { mutateAsync: archiveAuthorization } = useAuthorizationArchive();
    const { archiveHandler } = useArchiveState(archiveAuthorization);
    const [pagination, setPagination] =
      useState<DGT_Pagination>(DEF_PAGINATION);

    const { mutateAsync: downloadAuthorization } = useAuthorizationDownload();

    const handleDownloadOrderReport = async (authorizationId: string) => {
      downloadAuthorization({
        priorAuthorizationId: authorizationId,
      }).then((res) => {
        const blob = new Blob([res?.data], { type: "application/pdf" });
        const fileUrl = window.URL.createObjectURL(blob);
        previewResultDisc.open(fileUrl);
      });
    };
    const patientId = useParams()?.patientId || "";
    const { data: authorizationList, isLoading } = useAuthorizationList({
      pagination,
      archive: isArchived,
      authorizationStatus,
      patientId,
      xTENANTID: getXtenantId(),
    });

    // Column definitions for authorization table
    const AUTHORIZATION_COLUMNS: GridColDef[] = applyDefaultColumnSettings([
      {
        field: "insurancePayer",
        headerName: "Insurance Name",
        flex: 0.5,
        minWidth: 140,
        renderCell: ({ value }) => (
          <RenderWithTooltip
            value={value ? (getValueFromObj(value) ?? "-") : "-"}
          />
        ),
      },
      {
        field: "authorizationNumber",
        headerName: "Prior Auth Number",
        flex: 0.4,
        minWidth: 120,
        renderCell: ({ value }) => <RenderWithTooltip value={value || "-"} />,
      },
      {
        field: "allowedVisit",
        headerName: "Allowed Visit",
        flex: 0.3,
        minWidth: 100,
        renderCell: ({ value }) => value ?? "-",
      },
      {
        field: "warningCount",
        headerName: "Warning Count",
        flex: 0.3,
        minWidth: 100,
        renderCell: ({ value }) => value ?? "-",
      },
      {
        field: "remainingVisit",
        headerName: "Visit Remaining",
        flex: 0.3,
        minWidth: 100,
        renderCell: ({ value }) => value ?? "-",
      },
      {
        field: "cptCodes",
        headerName: "Procedure Code",
        flex: 0.45,
        minWidth: 130,
        renderCell: ({ value }) => {
          const labels = getAllValuesFromObj(value);
          return labels ? <RenderWithTooltip value={labels} /> : "-";
        },
      },
      {
        field: "provider",
        headerName: "Clinician Associated",
        flex: 0.5,
        minWidth: 140,
        renderCell: ({ value }) => (
          <RenderWithTooltip
            value={value ? (getValueFromObj(value) ?? "-") : "-"}
          />
        ),
      },
      {
        field: "startDate",
        headerName: "Start Date",
        flex: 0.3,
        minWidth: 100,
        renderCell: ({ value }) => (value ? NonNullableDate(value) : "-"),
      },
      {
        field: "endDate",
        headerName: "End Date",
        flex: 0.3,
        minWidth: 100,
        renderCell: ({ value }) => (value ? NonNullableDate(value) : "-"),
      },
      {
        field: "authorizationStatus",
        headerName: "Status",
        flex: 0.4,
        minWidth: 100,
        renderCell: ({ value }) =>
          value ? (
            <StatusChip
              label={replaceUnderScoreAddSpace(value.toLowerCase())}
              color={STATUS_COLOR_MAP[value as any]?.color}
              bgColor={STATUS_COLOR_MAP[value as any]?.bgColor}
            />
          ) : (
            "-"
          ),
      },
      {
        field: "actions",
        headerName: "Action",
        width: 70,
        align: "center",
        headerAlign: "center",
        hideSortIcons: true,
        renderCell: ({ row }) => (
          <ActionMenu
            options={getAuthorizationActionOptions(
              row.authorizationStatus,
              isArchived
            )}
            onActionClick={(v) => {
              if (v === AUTHORIZATION_ACTION.EDIT) {
                // todo : need to change from the backend for similar keys
                const payload = {
                  priorAuthorizationId: row.uuid,
                  authorizationNumber: row.authorizationNumber,
                  startDate: row.startDate,
                  endDate: row.endDate,
                  allowedVisit: row.allowedVisit ?? "",
                  warningCount: row.warningCount ?? "",
                  remainingVisit: row.remainingVisit ?? "",
                  notesComments: row.notes,
                  attachmentUpload: row.attachments,
                  requestingProvider: row?.provider
                    ? {
                        value: getIdFromObj(row.provider),
                        label: getValueFromObj(row.provider),
                      }
                    : undefined,
                  linkedEncounter: row.encounterId
                    ? JSON.parse(row.encounterId)
                    : undefined,
                  cptCodes: transformMultiSelectFrontEndVal(
                    DEFAULT_VALUE_LABEL_LIST,
                    row.cptCodes
                  ),
                  payerId: row?.insurancePayer
                    ? {
                        value: getIdFromObj(row.insurancePayer),
                        label: getValueFromObj(row.insurancePayer),
                      }
                    : undefined,
                  patientId: row.patientId,
                  fileName: row?.fileName ?? undefined,
                };

                authorizationDisclosure.open(payload);
              } else if (v === AUTHORIZATION_ACTION.ARCHIVE)
                deleteDisc.open({ uuid: row.uuid });
              else if (v === AUTHORIZATION_ACTION.VIEW)
                handleDownloadOrderReport(row.uuid);
            }}
          />
        ),
      },
    ]);

    const queryClient = useQueryClient();

    return (
      <>
        {authorizationDisclosure.opened && (
          <UpsertAuthorizationModal
            disClosureHost={authorizationDisclosure}
            title="Edit Authorization"
          />
        )}
        <PdfViewDrawer
          w="65vw"
          file={previewResultDisc?.data}
          disclosureHost={previewResultDisc}
          title="Order Result"
        />

        <DeleteModal
          w="380px"
          open={deleteDisc.opened}
          onClose={deleteDisc.close}
          id={deleteDisc.data?.uuid}
          onConfirmation={(uuid) =>
            archiveHandler(!isArchived, uuid).then((res) => {
              deleteDisc.close();
              queryClient.invalidateQueries({
                queryKey: [AUTHORIZATION_LIST_QUERY_KEY],
              });
              return res;
            })
          }
          content={`You want to ${
            isArchived ? "unarchive" : "archive"
          } this Prior Authorization`}
        />

        <Box
          sx={{
            height: "100%",
            width: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <CustomDataGridV2
            loading={isLoading}
            rows={authorizationList?.data?.content ?? []}
            rowCount={authorizationList?.data?.page?.totalElements ?? 10}
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            columns={(() => {
              let cols = AUTHORIZATION_COLUMNS;
              if (isVisitNotePage) {
                cols = cols.filter((col) =>
                  MINIMIZED_AUTHORIZATION_FIELDS.includes(col.field as string)
                );
              }
              return cols;
            })()}
            getRowId={(row) => row.uuid}
            disableRowSelectionOnClick
          />
        </Box>
      </>
    );
  }
);
